<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { ctaBannerDeposit } from '~/data/pages/banks/mocks'
import type { IResponseData } from '~/types'
import { WidgetSelectDepositType } from '~/components/widget/select-deposit-type'
import { WidgetOrganizationAsideContacts } from '~/components/widget/organization-aside-contacts'
import type { IDeposit } from '~/components/entity/deposit/item'
import { depositForWhomTranslations } from '~/components/entity/deposit'
import { useCurrencyProp } from '~/composables/useCurrencyProp'
import { WidgetAdsBlock } from '~/components/widget/banks-page'
import { LucideCircleAlert } from 'lucide-vue-next'
import { WidgetAboutPageHeader, mapItemToSchemaData } from '~/components/widget/about-page'
import {
  WidgetDepositsPageOptions,
  WidgetDepositPageHeaderSkeleton
} from '~/components/widget/deposits-page'
import { useCurrencyFormat } from '~/composables/useCurrencyFormat'
import { useSeoFromEntity } from '~/composables/useSeoFromEntity'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { FeatureDepositCalculator } from '~/components/feature/deposit/calculator'
import { WidgetDepositRatesTable } from '~/components/widget/deposit'
import { FeatureDepositFormRequest } from '~/components/feature/deposit/form-request'
import { WidgetRelatedProducts } from '~/components/widget/related-products'
import { Disclaimer } from '~/components/ui/disclaimer'
import { ContentBlock } from '~/components/ui/content-block'
import { WidgetRating } from '~/components/widget/rating'
import { WidgetDiscussionsAskSpecialist } from '~/components/widget/discussions'

const route = useRoute()
const { t, locale } = useI18n()
const depositUrl = `/deposits/${route.params.deposit}`

const organizationType = computed(() => {
  if (route.params.bank) return 'bank'
  if (route.params.microcredit) return 'microcredit'
  if (route.path.includes('/microcredit/')) return 'microcredit'
  if (route.path.includes('/bank/')) return 'bank'
  return 'bank'
})

const organizationSlug = computed(() => {
  return (route.params.bank || route.params.microcredit) as string
})

const { data, status } = await useApiWith404<IResponseData<IDeposit>>(() => depositUrl, {
  key: `deposit-${route.params.deposit}`
})

const deposit = computed(() => data.value?.data)
const config = useRuntimeConfig()
const baseUrl = config.public.siteUrl
useSeoFromEntity(deposit, baseUrl, locale.value)

const breadcrumbs = computed(() => {
  const orgSlug = organizationSlug.value
  const orgTitle = deposit.value?.organization.title

  if (organizationType.value === 'microcredit') {
    return [
      { title: t('common.microcredits'), url: '/microcredit' },
      {
        title: orgTitle || t('page.credits.detail.breadcrumbs_microcredit_organization'),
        url: orgSlug ? `/microcredit/${orgSlug}` : '/microcredit'
      },
      {
        title: t('common.deposits'),
        url: orgSlug ? `/microcredit/${orgSlug}/deposits` : '/deposits'
      },
      { title: deposit.value?.title || t('common.deposits_label'), url: deposit.value?.url }
    ]
  }

  return [
    { title: t('common.banks_of_kyrgyzstan'), url: '/banks' },
    {
      title: orgTitle || t('common.bank'),
      url: orgSlug ? `/bank/${orgSlug}` : '/banks'
    },
    {
      title: t('common.deposits'),
      url: orgSlug ? `/bank/${orgSlug}/deposits` : '/deposits'
    },
    { title: deposit.value?.title || t('common.deposits'), url: deposit.value?.url }
  ]
})

const expectedSlug = organizationSlug.value
if (
  deposit.value &&
  deposit.value.organization?.slug &&
  expectedSlug &&
  deposit.value.organization.slug !== expectedSlug
) {
  showError({ statusCode: 404 })
}

const depositForWhom = computed(() => {
  const result: string[] = []
  const forWhom = deposit.value?.for_whom
  if (forWhom) {
    for (const key in forWhom) {
      if (forWhom[key]) result.push(key)
    }
  }
  return result
})

const fitItem = computed(() => mapItemToSchemaData(deposit.value))

const isMicrocredit = computed(() => organizationType.value === 'microcredit')
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts v-if="deposit" :organization="deposit.organization" />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            <WidgetSelectDepositType :query-params="{ 'filter[position]': 'sidebar' }" />
            <WidgetDiscussionsAskSpecialist />
          </aside>
        </template>
        <div role="main">
          <div class="space-y-5">
            <WidgetAboutPageHeader v-if="deposit" :product="fitItem" type="deposit">
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-2">
                <div v-if="deposit && deposit.rates && deposit.rates.length">
                  <div class="SecondaryTextHeadings">
                    {{ $t('widget.credit_page.info_items.rate') }}
                  </div>
                  <div class="font-semibold">
                    {{ $t('widget.bank_page.offers.up_to') }} {{ deposit.best_rate_value }}%
                  </div>
                </div>
                <div v-if="deposit && deposit.rates && deposit.rates.length">
                  <div class="SecondaryTextHeadings">
                    {{ $t('page.deposits.detail.min_deposit_amount') }}
                  </div>
                  <div class="font-semibold">
                    {{ $t('common.from_amount') }}
                    {{
                      useCurrencyFormat(deposit.rates[0].amount_from, deposit.rates[0].currency_id)
                    }}
                  </div>
                </div>
                <div v-if="isMicrocredit && depositForWhom && depositForWhom.length > 0">
                  <div class="SecondaryTextHeadings">
                    {{ $t('widget.insurances.list_meili_search.for_whom') }}
                  </div>
                  <div class="font-semibold">
                    <span v-for="(item, index) in depositForWhom" :key="index">
                      {{ $t(depositForWhomTranslations[item]) }}
                      <span v-if="index < depositForWhom.length - 1">, </span>
                    </span>
                  </div>
                </div>
                <div v-if="deposit && deposit.rates && deposit.rates.length">
                  <div class="SecondaryTextHeadings">
                    {{ $t('page.deposits.detail.deposit_terms') }}
                  </div>
                  <div class="font-semibold">{{ deposit.max_period }}</div>
                </div>
                <div v-if="deposit && deposit.rates && deposit.rates.length">
                  <div class="SecondaryTextHeadings">{{ $t('common.currency') }}</div>
                  <div class="font-semibold">
                    {{ useCurrencyProp(deposit.rates[0].currency_id, 'title') }}
                  </div>
                </div>
                <div v-if="deposit">
                  <div class="SecondaryTextHeadings">
                    {{ $t('page.deposits.detail.payout_reward') }}
                  </div>
                  <div class="font-semibold">
                    {{ deposit.payout_cycle }},
                    {{ deposit.paid_to }}
                  </div>
                </div>
              </div>
            </WidgetAboutPageHeader>

            <WidgetDepositPageHeaderSkeleton v-else />

            <WidgetReviveAd zoneid="40" class="overflow-hidden rounded-lg" />

            <WidgetAdsBlock
              v-if="deposit && deposit.organization.permissions.is_deposit_request_allowed"
              :heading="$t(ctaBannerDeposit.heading)"
              :text="$t(ctaBannerDeposit.text)"
              :url="ctaBannerDeposit.url"
              :type="ctaBannerDeposit.type"
            />

            <section>
              <ContentBlock
                variant="white"
                :heading-text="$t('page.credits.detail.features_heading')"
                heading-class="h4"
                heading-tag="h2"
              >
                <Content>
                  <div v-html="deposit?.description" />
                  <div class="mt-5" v-html="deposit?.features" />
                </Content>

                <WidgetDepositsPageOptions
                  v-if="deposit && !deposit.types.some((i) => i.slug === 'islamic')"
                  :deposit="deposit"
                />

                <div v-if="!isMicrocredit && depositForWhom && depositForWhom.length > 0">
                  <Heading tag="h2" class="h4 mt-8">
                    {{ $t('page.deposits.detail.who_is_this_deposit_for') }}
                  </Heading>
                  <div v-for="(item, index) in depositForWhom" :key="index" class="mt-4 flex gap-2">
                    <LucideCircleAlert class="min-w-5 text-green-500" />
                    <p>{{ $t(depositForWhomTranslations[item]) }}</p>
                  </div>
                </div>

                <template v-if="isMicrocredit">
                  <div class="tex-sm flex gap-2">
                    <LucideCircleAlert class="min-w-5 text-orange-600" />
                    <p>
                      {{ $t('page.deposits.detail.additional_contributions_warning') }}
                    </p>
                  </div>
                  <div class="flex gap-2 text-sm">
                    <LucideCircleAlert class="min-w-5 text-orange-600" />
                    <p>
                      {{ $t('page.deposits.detail.early_termination_warning') }}
                    </p>
                  </div>
                </template>
              </ContentBlock>
            </section>

            <section v-if="deposit && deposit.rates && deposit.rates.length">
              <ContentBlock
                variant="white"
                :heading-text="
                  $t('page.deposits.detail.interest_on_deposit', { title: deposit.title })
                "
                heading-class="h4"
                heading-tag="h2"
              >
                <WidgetDepositRatesTable :rates="deposit.rates" />
              </ContentBlock>
            </section>

            <section v-if="deposit && deposit.rates && deposit.rates.length">
              <FeatureDepositCalculator
                :deposit="deposit"
                variant="white"
                heading-class="h4"
                heading-tag="h2"
              />
            </section>

            <WidgetRating
              v-if="deposit && config.public.widgetRatingEnabled"
              :entity="deposit"
              entity-type="deposit"
              :heading-text="$t('page.deposit.rating_heading', { title: deposit.title })"
            />
            <Disclaimer
              :title="$t('common.product.info_disclaimer.title')"
              :description="$t('common.product.info_disclaimer.description')"
            />
            <div
              v-if="
                deposit &&
                deposit.rates &&
                deposit.organization.permissions.is_deposit_request_allowed &&
                deposit.organization.status === 'active' &&
                deposit.status === 'active'
              "
            >
              <FeatureDepositFormRequest
                :available-currencies="deposit.currencies"
                :deposit="deposit"
                variant="white"
                :heading-text="
                  $t('page.deposits.detail.apply_for_deposit', { title: deposit.title })
                "
                heading-class="h4"
                heading-tag="h2"
              />
            </div>
            <WidgetRelatedProducts
              v-if="deposit?.related_products && deposit?.related_products.length"
              :products="deposit.related_products"
              :status="status"
            />
          </div>
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
